import * as _ from 'lodash';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { getFormValuesFromCommitment } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/dataMappings/getFormValues';
import { CommitmentMechanicsFormData } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/types';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { computeDiff } from '~/core/routes/settings/implementations/common/DiffInputs/hooks';

export const usePreviousAndCurrentCommitmentFormData = (
  commitmentKey: string | undefined,
) => {
  const creditAgreement = useCreditAgreementStore(
    (store) => store.creditAgreement,
  );
  const previousCreditAgreement = useCreditAgreementStore(
    (store) => store.previousCreditAgreement,
  );

  const commitments = creditAgreement?.loanMechanicsV1?.commitments ?? [];
  const previousCommitments =
    previousCreditAgreement?.loanMechanicsV1?.commitments ?? [];

  const commitment = commitments.find((c) => c.key === commitmentKey);
  const previousCommitment = previousCommitments.find(
    (c) => c.key === commitmentKey,
  );

  const commitmentFormData =
    commitment && creditAgreement
      ? getFormValuesFromCommitment(commitment, creditAgreement)
      : null;
  const previousCommitmentFormData =
    previousCommitment && previousCreditAgreement
      ? getFormValuesFromCommitment(previousCommitment, previousCreditAgreement)
      : null;

  return { commitmentFormData, previousCommitmentFormData };
};

export const useProductDiffResult = (pricingOptionKey: string | undefined) => {
  const form = useFormContext<CommitmentMechanicsFormData>();
  const commitmentKey = form.getValues('wipForm.key');

  const {
    commitmentFormData,
    previousCommitmentFormData,
  } = usePreviousAndCurrentCommitmentFormData(commitmentKey);

  const currentProduct = commitmentFormData?.products?.find(
    (p) => p?.pricingOptionKey === pricingOptionKey,
  );

  const previousProduct = previousCommitmentFormData?.products?.find(
    (p) => p?.pricingOptionKey === pricingOptionKey,
  );

  const diffResult = computeDiff(
    currentProduct,
    previousProduct,
    _.isEqual,
    String,
  );

  return useMemo(() => ({ diffResult, currentProduct, previousProduct }), [
    diffResult,
    currentProduct,
    previousProduct,
  ]);
};

export const useCommitmentDiffResult = (commitmentKey: string | undefined) => {
  const {
    commitmentFormData,
    previousCommitmentFormData,
  } = usePreviousAndCurrentCommitmentFormData(commitmentKey);

  const diffResult = computeDiff(
    commitmentFormData,
    previousCommitmentFormData,
    _.isEqual,
    String,
  );

  return useMemo(
    () => ({
      diffResult,
      commitmentFormData,
      previousCommitmentFormData,
    }),
    [diffResult, commitmentFormData, previousCommitmentFormData],
  );
};

export const useServicingFeeDiffResult = (lenderId: string | undefined) => {
  const form = useFormContext<CommitmentMechanicsFormData>();
  const commitmentKey = form.getValues('wipForm.key');

  const {
    commitmentFormData,
    previousCommitmentFormData,
  } = usePreviousAndCurrentCommitmentFormData(commitmentKey);

  const currentFee = commitmentFormData?.servicingFees?.find(
    (s) => s?.lenderId === lenderId,
  );

  const previousFee = previousCommitmentFormData?.servicingFees?.find(
    (s) => s?.lenderId === lenderId,
  );

  const diffResult = computeDiff(currentFee, previousFee, _.isEqual, String);

  return useMemo(
    () => ({
      diffResult,
      currentFee,
      previousFee,
    }),
    [diffResult, currentFee, previousFee],
  );
};
